import { useCallback, useEffect } from 'react'
import { Modal as BootstrapModal } from 'react-bootstrap'

import { Modal } from '../Modal/Modal'
import { Button } from '../Button/Button'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import {
  resetFileBrowser,
  selectFileBrowserSelectedItems,
  selectFileBrowserBreadcrumbs,
  selectFileBrowserIsAtRoot,
} from '../../store/documentFileBrowser/documentFileBrowserSlice'
import { type IntegrationServiceId, getIntegrationDisplayName } from '../../common/integrations'
import { type DocumentConnectorIntegration } from '../../store/documentConnectors/documentConnectors'
import { type FileBrowserItem, type SelectionMode } from './types'
import { IntegrationFileBrowser } from './IntegrationFileBrowser'
import styles from './DocumentFileBrowserModal.module.scss'

const getConfirmButtonText = (selectionMode: SelectionMode, count: number) => {
  switch (selectionMode) {
    case 'single-file':
      return 'Select File'
    case 'multi-file':
      return 'Add files'
    case 'single-folder':
      return 'Select Folder'
    case 'multi-folder':
      return 'Select Folders'
    case 'any':
      return count > 0 ? `Select ${count} item${count !== 1 ? 's' : ''}` : 'Select Items'
  }
}

interface DocumentFileBrowserModalProps {
  show: boolean
  onHide: () => void
  serviceId: IntegrationServiceId
  selectionMode?: SelectionMode
  onFilesSelected: (files: FileBrowserItem[]) => void
  initialFolderIds?: string[]
  initialFoldersReady?: boolean
  contactName?: string
  linkedFolderId?: string | null
  onLinkFolder?: (integration: DocumentConnectorIntegration) => Promise<void>
}

export const DocumentFileBrowserModal = ({
  show,
  onHide,
  serviceId,
  selectionMode = 'multi-file',
  onFilesSelected,
  initialFolderIds,
  initialFoldersReady,
  contactName,
  linkedFolderId,
  onLinkFolder,
}: DocumentFileBrowserModalProps) => {
  const dispatch = useAppDispatch()
  const selectedItems = useAppSelector(selectFileBrowserSelectedItems)
  const breadcrumbs = useAppSelector(selectFileBrowserBreadcrumbs)
  const isAtRoot = useAppSelector(selectFileBrowserIsAtRoot)

  const integrationDisplayName = getIntegrationDisplayName(serviceId)

  const isSingleFolder = selectionMode === 'single-folder'
  const currentCrumb = breadcrumbs[breadcrumbs.length - 1]
  const folderSelection: FileBrowserItem[] =
    isSingleFolder && !isAtRoot && currentCrumb ? [{ id: currentCrumb.id, name: currentCrumb.name, type: 'folder', integration: currentCrumb.integration }] : []
  const effectiveItems = isSingleFolder ? folderSelection : selectedItems

  useEffect(() => {
    return () => {
      dispatch(resetFileBrowser())
    }
  }, [dispatch])

  const buttonLabel = getConfirmButtonText(selectionMode, effectiveItems.length)

  const handleConfirm = useCallback(() => {
    if (effectiveItems.length === 0) {
      return
    }
    onFilesSelected(effectiveItems)
    onHide()
  }, [onFilesSelected, onHide, effectiveItems])

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      dialogClassName={styles.documentFileBrowserModal}
      bodyClassName={styles.documentFileBrowserBody}
      header={<BootstrapModal.Title>Browse {integrationDisplayName} Documents</BootstrapModal.Title>}
      body={
        <IntegrationFileBrowser
          serviceId={serviceId}
          selectionMode={selectionMode}
          initialFolderIds={initialFolderIds}
          initialFoldersReady={initialFoldersReady}
          contactName={contactName}
          linkedFolderId={linkedFolderId}
          onLinkFolder={onLinkFolder}
        />
      }
      footer={
        <div className='d-flex zocks-gap-0.75'>
          <Button variant='secondaryGhostButton' onClick={onHide}>
            Cancel
          </Button>
          <Button variant='primary' onClick={handleConfirm} disabled={effectiveItems.length === 0}>
            {buttonLabel}
          </Button>
        </div>
      }
    />
  )
}
